import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { TimePeriod } from '@/components/reit-value-trend-chart/REITValueTrendChart';
import {ReitDividendPayment} from "@/@types/reit.models.ts";

function filterByPeriod(items: DividendDataPoint[], period: TimePeriod): DividendDataPoint[] {
  if (period === 'ALL') return items;
  const now = new Date();
  const months = period === '3M' ? 3 : period === '6M' ? 6 : 12;
  const cutoff = new Date(now.getFullYear(), now.getMonth() - months, now.getDate());
  return items.filter((item) => new Date(item.date) >= cutoff);
}

export interface REITDividendHistoryChartProps {
  payments: ReitDividendPayment[];
  period: TimePeriod;
  currency: string;
}

interface DividendDataPoint {
  date: string;
  amount: number;
}

function buildChartData(payments: ReitDividendPayment[]): DividendDataPoint[] {
  return payments.map((p) => ({ date: p.payment_date, amount: p.total_amount }));
}

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function CustomTooltip({ active, payload, currency }: any) {
  if (!active || !payload?.length) return null;
  const { date, amount } = payload[0].payload as DividendDataPoint;
  const formatted = new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="text-sm text-muted-foreground mb-1">{formatted}</p>
      <p className="text-base font-semibold">{formatCurrency(amount, currency)}</p>
    </div>
  );
}

export function REITDividendHistoryChart({ payments, period, currency }: REITDividendHistoryChartProps) {
  const allPoints = buildChartData(payments);
  const filtered = filterByPeriod(allPoints, period);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dividend History</CardTitle>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <div
            className="flex items-center justify-center h-[300px] text-muted-foreground text-sm text-center px-4"
            data-testid="empty-state"
          >
            No dividend payments recorded yet.
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <div className="min-w-[500px]">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={filtered}
                  margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v) =>
                      new Date(v).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                    }
                    className="text-xs"
                  />
                  <YAxis
                    tickFormatter={(v) => formatCurrency(v, currency)}
                    className="text-xs"
                    width={90}
                  />
                  <Tooltip content={<CustomTooltip currency={currency} />} />
                  <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
